import { ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { initApiService } from '@/services/init'
import { useAnnouncer } from './useAnnouncer'
import type { ClientProfile } from '@/api/generated-clients'
import { logUnexpectedFailure } from '@/utils/errorHandling'

export interface UseClientSearchOptions {
  /** Minimum query length to trigger search (default: 2) */
  minQueryLength?: number
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number
  /** IDs of clients to exclude from results */
  excludeIds?: string[]
}

/**
 * Composable for searching clients with debouncing and keyboard navigation.
 *
 * Provides functionality to:
 * - Search clients by query with automatic debouncing
 * - Navigate search results with keyboard (up/down arrows)
 * - Select the currently highlighted result
 *
 * Usage:
 * ```vue
 * <script setup>
 * import { useClientSearch } from '@/composables/useClientSearch'
 *
 * const {
 *   query,
 *   results,
 *   loading,
 *   selectedIndex,
 *   search,
 *   selectNext,
 *   selectPrevious,
 *   selectCurrent,
 *   reset
 * } = useClientSearch({ excludeIds: existingMemberIds })
 * </script>
 * ```
 */
export function useClientSearch(options: UseClientSearchOptions = {}) {
  const { minQueryLength = 2, debounceMs = 300, excludeIds = [] } = options

  const query = ref('')
  const results = ref<ClientProfile[]>([])
  const loading = ref(false)
  const selectedIndex = ref(0)
  const { announce } = useAnnouncer()

  /**
   * Perform the actual API search
   */
  async function performSearch(): Promise<void> {
    if (query.value.length < minQueryLength) {
      results.value = []
      return
    }

    loading.value = true
    selectedIndex.value = 0

    try {
      const api = initApiService()
      const response = await api.clients.listClients(
        0,
        20,
        'lastName,asc',
        query.value,
        undefined,
        false
      )

      // Filter out excluded clients
      results.value = (response.data.content ?? []).filter(
        (client) => !excludeIds.includes(client.id ?? '')
      )

      // Announce search results for screen readers
      const count = results.value.length
      if (count === 0) {
        announce('Geen resultaten gevonden')
      } else if (count === 1) {
        announce('1 resultaat gevonden')
      } else {
        announce(`${count} resultaten gevonden`)
      }
    } catch (err) {
      logUnexpectedFailure('Error searching clients:', err)
      results.value = []
    } finally {
      loading.value = false
    }
  }

  /**
   * Debounced search function - call this on input changes
   */
  const search = useDebounceFn(performSearch, debounceMs)

  /**
   * Move selection to next result (wraps around)
   */
  function selectNext(): void {
    if (results.value.length === 0) return
    selectedIndex.value = (selectedIndex.value + 1) % results.value.length
  }

  /**
   * Move selection to previous result (wraps around)
   */
  function selectPrevious(): void {
    if (results.value.length === 0) return
    selectedIndex.value =
      (selectedIndex.value - 1 + results.value.length) % results.value.length
  }

  /**
   * Get the currently selected client
   */
  function selectCurrent(): ClientProfile | null {
    if (results.value.length === 0) return null
    return results.value[selectedIndex.value] ?? null
  }

  /**
   * Reset the search state
   */
  function reset(): void {
    query.value = ''
    results.value = []
    loading.value = false
    selectedIndex.value = 0
  }

  // Reset selection index when results change
  watch(results, () => {
    selectedIndex.value = 0
  })

  return {
    query,
    results,
    loading,
    selectedIndex,
    search,
    selectNext,
    selectPrevious,
    selectCurrent,
    reset,
  }
}
